import { useEffect, useState } from 'react'
import { useLibraryStore } from '@/stores/library-store'
import { FolderTreeSidebar } from '@/components/FolderTreeSidebar'
import { DocumentPanel } from '@/components/DocumentPanel'
import { LibraryToolbar } from '@/components/LibraryToolbar'
import { useResizablePanel } from '@/hooks/useResizablePanel'
import { useDropZoneUpload } from '@/hooks/useDropZoneUpload'
import { CreateFolderDialog } from '@/components/CreateFolderDialog'
import { UploadDestinationDialog } from '@/components/UploadDestinationDialog'
import { DuplicateUploadDialog } from '@/components/DuplicateUploadDialog'
import { Loader2 } from 'lucide-react'

export function LibraryPage() {
  const {
    selectedFolderId,
    fetchFolders,
    fetchDocuments,
    fetchTags,
    startStatusPolling,
    stopStatusPolling,
    isLoading,
    error,
  } = useLibraryStore()

  const { width: sidebarWidth, isResizing, handleProps } = useResizablePanel({
    defaultWidth: 280,
    minWidth: 220,
    maxWidth: 420,
    storageKey: 'library-sidebar-width',
  })

  const [stagedFiles, setStagedFiles] = useState<File[]>([])
  const [uploadDestDialogOpen, setUploadDestDialogOpen] = useState(false)

  // Lifted dropzone — toolbar's Upload button triggers `open()`, DocumentPanel uses
  // the same instance for the drag-and-drop overlay (so we only register one
  // react-dropzone listener and share error state).
  //
  // When no folder is selected, files are staged and the destination dialog
  // opens for explicit folder selection instead of auto-uploading.
  const dropzone = useDropZoneUpload({
    folderId: selectedFolderId,
    onFilesStaged: (files) => {
      setStagedFiles(files)
      setUploadDestDialogOpen(true)
    },
  })

  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false)
  const [createFolderDefaultParent, setCreateFolderDefaultParent] =
    useState<string | null>(null)

  /** Open the unified Create Folder dialog with an explicit default parent. */
  const openCreateFolder = (defaultParentId: string | null) => {
    setCreateFolderDefaultParent(defaultParentId)
    setCreateFolderDialogOpen(true)
  }

  useEffect(() => {
    const init = async () => {
      await Promise.all([fetchFolders(), fetchDocuments(), fetchTags()])
      startStatusPolling() // Start polling if any pending docs exist
    }
    void init()

    return () => {
      stopStatusPolling() // Cleanup on unmount
    }
  }, [fetchFolders, fetchDocuments, fetchTags, startStatusPolling, stopStatusPolling])

  if (isLoading && !useLibraryStore.getState().folders.length) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground mt-2">Loading library...</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full p-4 gap-3">
      {/*
        Hidden file input owned by the lifted dropzone. MUST live at the page
        level so `dropzone.open()` works regardless of whether the document
        panel or the overview is rendered — react-dropzone's `open()` clicks
        the input ref under the hood, and the ref only exists where
        getInputProps() is rendered.
      */}
      <input {...dropzone.getInputProps()} />
      {dropzone.folderInput}

      {/* Toolbar — owns all page-level actions. Upload is always enabled now:
          if a folder is selected, files auto-upload there. If not, the dropzone
          stages them and opens the destination dialog. */}
      <LibraryToolbar
        onOpenFilePicker={dropzone.open}
        onOpenFolderPicker={dropzone.openFolderPicker}
        canUpload
        onNewFolder={() => openCreateFolder(selectedFolderId)}
      />

      {/* Error Banner */}
      {error && (
        <div className="rounded-lg bg-destructive/10 text-destructive text-sm px-4 py-2">
          {error}
        </div>
      )}

      {/* Main Content — single shell containing folder tree + document panel */}
      <div className="flex flex-1 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        <FolderTreeSidebar
          width={sidebarWidth}
          handleProps={handleProps}
          isResizing={isResizing}
          onNewRootFolder={() => openCreateFolder(null)}
          onNewSubfolder={(parentId) => openCreateFolder(parentId)}
        />
        <DocumentPanel dropzone={dropzone} />
      </div>

      <CreateFolderDialog
        open={createFolderDialogOpen}
        onOpenChange={setCreateFolderDialogOpen}
        defaultParentId={createFolderDefaultParent}
      />

      <UploadDestinationDialog
        open={uploadDestDialogOpen}
        onOpenChange={(o) => {
          setUploadDestDialogOpen(o)
          if (!o) setStagedFiles([])
        }}
        files={stagedFiles}
        defaultFolderId={selectedFolderId}
      />

      {/* F4 — self-gating: renders only while a same-name collision is queued.
          Lives here so every upload entry point (toolbar, drop zone, folder
          drop, destination dialog) is covered by one dialog. */}
      <DuplicateUploadDialog />
    </div>
  )
}
